/**
 * QR Trade encoding/decoding utilities.
 *
 * NEW format (server-side sessions):
 *   URL: /?qr=s_<12 hex chars>
 *   The full payload is stored in the `qr_sessions` table; the QR only
 *   encodes the short ID so it works with albums of any size.
 *
 * LEGACY format (kept for backward compatibility):
 *   URL: /?qr=<base64url>
 *   Payload: "<globalAlbumId>|<missingRanges>|<dupeRanges>"
 */

import { supabase } from "@/lib/supabase";

function numbersToRanges(nums: number[]): string {
  if (nums.length === 0) return "";
  const sorted = [...nums].sort((a, b) => a - b);
  const ranges: string[] = [];
  let start = sorted[0];
  let end = sorted[0];
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] === end + 1) {
      end = sorted[i];
    } else {
      ranges.push(start === end ? `${start}` : `${start}-${end}`);
      start = sorted[i];
      end = sorted[i];
    }
  }
  ranges.push(start === end ? `${start}` : `${start}-${end}`);
  return ranges.join(",");
}

function rangesToNumbers(rangeStr: string): number[] {
  if (!rangeStr) return [];
  const result: number[] = [];
  const tokens = rangeStr.split(",");
  const rangeRe = /^(\d+)-(\d+)$/;
  for (const token of tokens) {
    const m = rangeRe.exec(token.trim());
    if (m) {
      const from = Number.parseInt(m[1], 10);
      const to = Number.parseInt(m[2], 10);
      for (let i = from; i <= to; i++) result.push(i);
    } else {
      const n = Number.parseInt(token.trim(), 10);
      if (!Number.isNaN(n)) result.push(n);
    }
  }
  return result;
}

export interface QrTradePayload {
  globalAlbumId: string;
  missing: number[];
  duplicates: number[];
  /** User ID of the QR generator — present only in server-side sessions. */
  userId?: string;
}

export function encodeQrTrade(payload: QrTradePayload): string {
  const raw = [
    payload.globalAlbumId,
    numbersToRanges(payload.missing),
    numbersToRanges(payload.duplicates),
  ].join("|");
  const bytes = new TextEncoder().encode(raw);
  let binary = "";
  bytes.forEach((b) => {
    binary += String.fromCodePoint(b);
  });
  return btoa(binary)
    .replaceAll("+", "-")
    .replaceAll("/", "_")
    .replace(/=+$/, "");
}

export function decodeQrTrade(encoded: string): QrTradePayload | null {
  try {
    // Restore base64url to base64
    const base64 = encoded.replaceAll("-", "+").replaceAll("_", "/");
    const binary = atob(base64);
    const bytes = Uint8Array.from(binary, (c) => c.codePointAt(0) ?? 0);
    const raw = new TextDecoder().decode(bytes);
    const [globalAlbumId, missingStr, dupesStr] = raw.split("|");
    if (!globalAlbumId) return null;
    return {
      globalAlbumId,
      missing: rangesToNumbers(missingStr ?? ""),
      duplicates: rangesToNumbers(dupesStr ?? ""),
    };
  } catch {
    return null;
  }
}

export function buildQrUrl(payload: QrTradePayload): string {
  const encoded = encodeQrTrade(payload);
  const base = globalThis.location.origin + globalThis.location.pathname;
  return `${base}?qr=${encoded}`;
}

// ---------------------------------------------------------------------------
// Server-side session helpers (preferred — no QR size limit)
// ---------------------------------------------------------------------------

/** Returns true if the qr param is a server-side session ID (starts with "s_"). */
export function isQrSessionId(value: string): boolean {
  return value.startsWith("s_");
}

/**
 * Stores the full payload in `qr_sessions` and returns the short URL.
 * The QR code only encodes the short ID — works for albums of any size.
 */
export async function createQrSession(
  payload: QrTradePayload,
  userId?: string,
): Promise<string> {
  const id = "s_" + crypto.randomUUID().replace(/-/g, "").slice(0, 12);
  const { error } = await supabase.from("qr_sessions").insert({
    id,
    global_album_id: payload.globalAlbumId,
    missing: payload.missing,
    duplicates: payload.duplicates,
    user_id: userId ?? null,
  });
  if (error) throw error;
  const base = globalThis.location.origin + globalThis.location.pathname;
  return `${base}?qr=${id}`;
}

/** Fetches a session payload by its short ID. */
export async function fetchQrSession(
  id: string,
): Promise<QrTradePayload | null> {
  const { data, error } = await supabase
    .from("qr_sessions")
    .select("global_album_id, missing, duplicates, user_id")
    .eq("id", id)
    .single();
  if (error || !data) return null;
  return {
    globalAlbumId: data.global_album_id,
    missing: data.missing,
    duplicates: data.duplicates,
    userId: data.user_id ?? undefined,
  };
}

/**
 * Resolves a `?qr=` param value to a payload.
 * Handles both new session IDs and legacy base64url format.
 */
export async function resolveQrParam(
  value: string,
): Promise<QrTradePayload | null> {
  if (isQrSessionId(value)) {
    return fetchQrSession(value);
  }
  // Legacy base64url format — synchronous, wrapped in a promise for uniformity.
  return decodeQrTrade(value);
}
